import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import { FileWarning } from "lucide-react";

export const FIT_SCALE = 0.88;

const RENDER_OPTIONS = {
  className: "docx",
  inWrapper: true,
  breakPages: true,
  renderHeaders: true,
  renderFooters: true,
  renderFootnotes: true,
  ignoreWidth: false,
  ignoreHeight: false,
  experimental: true,
  useBase64URL: true,
  // Security: uploaded CVs are untrusted. An altChunk is raw HTML embedded in the .docx, which
  // docx-preview renders into an unsandboxed same-origin <iframe srcdoc> — script in it would run
  // inside the staff session. On by default, so it has to be switched off explicitly.
  renderAltChunks: false,
};

const SAFE_LINK = /^(https?:|mailto:|tel:|#)/i;
const UNSAFE_ELEMENTS = "script, iframe, frame, object, embed, base, form, link, meta";

// docx-preview copies a hyperlink's target from the file straight into `href`, so a
// `javascript:` link would run on click. Only plain web/mail/phone links and in-document anchors
// survive; anything else keeps its text and loses the link. The element sweep is defence in depth
// in case a future version of the library renders more of the document than it does today.
const sanitizeRenderedDocx = (root) => {
  root.querySelectorAll(UNSAFE_ELEMENTS).forEach((el) => el.remove());

  root.querySelectorAll("a[href]").forEach((link) => {
    const href = link.getAttribute("href").trim();
    if (!SAFE_LINK.test(href)) {
      link.removeAttribute("href");
      return;
    }
    if (!href.startsWith("#")) {
      link.setAttribute("target", "_blank");
      link.setAttribute("rel", "noopener noreferrer");
    }
  });
};

const DocxViewer = ({ fileUrl }) => {
  const [status, setStatus] = useState("loading");
  const scrollRef = useRef(null);
  const bodyRef = useRef(null);
  const styleRef = useRef(null);

  const fit = useCallback(() => {
    const scroller = scrollRef.current;
    const body = bodyRef.current;
    if (!scroller || !body) return;

    body.style.transform = "";
    body.style.height = "";

    const page = body.querySelector(".docx-wrapper > section, .docx-wrapper > .docx");
    const pageWidth = page?.offsetWidth || body.scrollWidth;
    if (!pageWidth) return;

    const available = (scroller.clientWidth - 24) * FIT_SCALE;
    const scale = available / pageWidth;
    if (!Number.isFinite(scale) || scale <= 0) return;

    const naturalHeight = body.offsetHeight;
    body.style.transformOrigin = "top center";
    body.style.transform = `scale(${scale})`;
    body.style.height = `${naturalHeight * scale}px`;
  }, []);

  useEffect(() => {
    if (!fileUrl) return undefined;

    let active = true;
    setStatus("loading");

    const render = async () => {
      try {
        const [{ renderAsync }, response] = await Promise.all([
          import("docx-preview"),
          fetch(fileUrl, { credentials: "same-origin" }),
        ]);

        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        const buffer = await response.arrayBuffer();
        if (!active || !bodyRef.current) return;

        bodyRef.current.innerHTML = "";
        bodyRef.current.style.transform = "";
        bodyRef.current.style.height = "";

        await renderAsync(buffer, bodyRef.current, styleRef.current, RENDER_OPTIONS);
        sanitizeRenderedDocx(bodyRef.current);
        if (!active) return;

        setStatus("ready");
        fit();
      } catch {
        if (active) setStatus("error");
      }
    };

    render();

    return () => {
      active = false;
    };
  }, [fileUrl, fit]);

  useEffect(() => {
    const scroller = scrollRef.current;
    if (!scroller || status !== "ready") return undefined;

    const observer = new ResizeObserver(fit);
    observer.observe(scroller);

    return () => observer.disconnect();
  }, [fit, status]);

  return (
    <Box sx={{ position: "relative", flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
      <Box ref={styleRef} sx={{ display: "none" }} />

      {status === "loading" && (
        <Box sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <CircularProgress size={22} sx={{ color: "hsl(150 38% 17%)" }} />
        </Box>
      )}

      {status === "error" && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
            px: 4,
            textAlign: "center",
          }}
        >
          <FileWarning size={22} color="hsl(35 92% 40%)" />
          <Typography sx={{ fontSize: 13, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: "hsl(150 30% 12%)" }}>
            This Word file could not be displayed
          </Typography>
          <Typography sx={{ fontSize: 12, color: "hsl(150 10% 46%)" }}>
            Open the Text tab to read what the parser extracted.
          </Typography>
        </Box>
      )}

      <Box
        ref={scrollRef}
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: "auto",
          py: 1.5,
          visibility: status === "ready" ? "visible" : "hidden",
          "& .docx-wrapper": { background: "transparent", padding: 0, display: "flex", flexDirection: "column", alignItems: "center", gap: "12px" },
          "& .docx-wrapper > section": { boxShadow: "0 1px 3px rgba(16,24,40,0.16)", marginBottom: 0 },
        }}
      >
        <Box ref={bodyRef} />
      </Box>
    </Box>
  );
};

export default DocxViewer;
